"use client"

import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import { cn } from "@/lib/utils"
import {
  ReportIcon,
  Report2Icon,
  BodyFigureIcon,
  SupplementsIcon,
  ActionPlanIcon,
  LifestyleIcon,
} from "@/components/ui/icons"

import type { ComponentType } from "react"
import { blobMediaUrl } from "@/lib/blob-url"

type IconComponent = ComponentType<{ className?: string; size?: number }>

export interface ScatteredDataProps {
  heading: string
  headingHighlight: string
  subtitle: string
  pillLabels: {
    medicalRecord: string
    /** Omitted in blood-only markets (DE): there is no scan to collect, so the
     *  left column funnels two sources into the card instead of three. */
    mriScan?: string
    prescriptions: string
    bloodWork: string
    actionPlan: string
    lifestyleGoals: string
  }
  cardName: string
  cardBiomarkers: string
  cardActionPoints: string
  variant?: "green" | "light"
}

/* ── Pill icon mapping (design-system icons) ── */
const ICONS: Record<string, IconComponent> = {
  medicalRecord: ReportIcon,
  mriScan: BodyFigureIcon,
  prescriptions: SupplementsIcon,
  bloodWork: Report2Icon,
  actionPlan: ActionPlanIcon,
  lifestyleGoals: LifestyleIcon,
}

/*
 * SVG coordinate system: viewBox="0 0 1000 200"
 *
 * Left pills right-edge ≈ x=200, y = 25 / 100 / 175
 * Right pills left-edge ≈ x=800, y = 25 / 100 / 175
 * Left convergence = 290, right convergence = 710
 * Left stem 290→340, right stem 710→660
 * Card centred at 340–660
 */

/* Branches: pill → convergence */
const L_TOP = "M 200 25 L 250 25 C 270 25 270 100 290 100"
const L_MID = "M 200 100 L 290 100"
const L_BOT = "M 200 175 L 250 175 C 270 175 270 100 290 100"
const L_STEM = "M 290 100 L 340 100"

const R_TOP = "M 800 25 L 750 25 C 730 25 730 100 710 100"
const R_MID = "M 800 100 L 710 100"
const R_BOT = "M 800 175 L 750 175 C 730 175 730 100 710 100"
const R_STEM = "M 710 100 L 660 100"

export function ScatteredData({
  heading,
  headingHighlight,
  subtitle,
  pillLabels,
  cardName,
  cardBiomarkers,
  cardActionPoints,
  variant = "green",
}: ScatteredDataProps) {
  const isLight = variant === "light"
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          obs.unobserve(el)
        }
      },
      { threshold: 0.35 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  /* Mobile: the incoming sources above the card, the outputs below. Grouped so
     connector x-positions align with column centers regardless of label length.
     The top row is 3 pills, or 2 when `mriScan` is omitted (blood-only
     markets) — both rows keep the 3-column grid so the outer columns stay put. */
  const pillsMobileTop = [
    { label: pillLabels.medicalRecord, icon: ICONS.medicalRecord },
    ...(pillLabels.mriScan
      ? [{ label: pillLabels.mriScan, icon: ICONS.mriScan }]
      : []),
    { label: pillLabels.prescriptions, icon: ICONS.prescriptions },
  ]
  const pillsMobileBottom = [
    { label: pillLabels.bloodWork, icon: ICONS.bloodWork },
    { label: pillLabels.actionPlan, icon: ICONS.actionPlan },
    { label: pillLabels.lifestyleGoals, icon: ICONS.lifestyleGoals },
  ]

  /* Pills positioned so their inner edges align with SVG path starts (x=200 / x=800 in viewBox 1000).
     right: 80% puts the pill's right edge at 20% from the left = x=200 in SVG coords.
     left: 80% puts the pill's left edge at 80% from the left = x=800 in SVG coords.
     A two-pill left column takes the outer rows (7 / 157) and the outer
     connectors, so the funnel stays symmetric instead of leaving a gap where
     the scan row was. */
  const leftTops = pillLabels.mriScan ? [7, 82, 157] : [7, 157]
  const leftBranchIds = pillLabels.mriScan
    ? ["sd-lt", "sd-lm", "sd-lb"]
    : ["sd-lt", "sd-lb"]
  const pillsLeft = [
    { label: pillLabels.medicalRecord, icon: ICONS.medicalRecord },
    ...(pillLabels.mriScan
      ? [{ label: pillLabels.mriScan, icon: ICONS.mriScan }]
      : []),
    { label: pillLabels.prescriptions, icon: ICONS.prescriptions },
  ].map((pill, i) => ({ ...pill, top: leftTops[i], right: "80%" as const }))
  const mobileTopBranches: ("l" | "m" | "r")[] =
    pillsMobileTop.length === 3 ? ["l", "m", "r"] : ["l", "r"]

  const pillsRight = [
    {
      label: pillLabels.bloodWork,
      icon: ICONS.bloodWork,
      top: 7,
      left: "80%" as const,
    },
    {
      label: pillLabels.actionPlan,
      icon: ICONS.actionPlan,
      top: 82,
      left: "80%" as const,
    },
    {
      label: pillLabels.lifestyleGoals,
      icon: ICONS.lifestyleGoals,
      top: 157,
      left: "80%" as const,
    },
  ]

  return (
    <>
      <style>{`
        @keyframes sd-flowSync {
          from { stroke-dashoffset: 200; }
          to   { stroke-dashoffset: 0; }
        }
        @keyframes sd-stemFlow {
          0%, 40%  { stroke-dashoffset: 100; }
          50%      { stroke-dashoffset: 0; }
          65%      { stroke-dashoffset: -100; }
          100%     { stroke-dashoffset: -100; }
        }
        .sd-base, .sd-flow, .sd-stem {
          stroke-width: 3.5;
          stroke-linecap: round;
          fill: none;
        }
        .sd-base { stroke: #d2d9a8; }
        .sd-flow {
          stroke: var(--color-bright-green-500);
          stroke-dasharray: 20 180;
          animation: sd-flowSync 6s linear infinite;
        }
        .sd-stem {
          stroke: var(--color-bright-green-500);
          stroke-dasharray: 100 100;
          animation: sd-stemFlow 6s linear infinite;
        }
        /* Mobile connectors: thinner stroke, non-scaling so it stays crisp
           despite the SVG's non-uniform preserveAspectRatio. */
        .sd-m-base, .sd-m-flow {
          stroke-width: 2.25;
          stroke-linecap: round;
          fill: none;
          vector-effect: non-scaling-stroke;
        }
        .sd-m-base { stroke: #d2d9a8; }
        .sd-m-flow {
          stroke: var(--color-bright-green-500);
          stroke-dasharray: 14 120;
          animation: sd-flowSync 5s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .sd-flow, .sd-stem, .sd-m-flow { animation: none; }
        }
      `}</style>
      <div
        ref={ref}
        className={cn(
          "mx-auto max-w-[1038px]",
          isLight
            ? "rounded-none bg-transparent px-4 pb-5 md:px-0"
            : "bg-bright-green-600 rounded-[32px] px-6 py-10 md:px-[60px] md:py-[52px]"
        )}
      >
        {/* Header */}
        <div className="mb-12 text-center">
          <h2 className="type-display-md">
            <span className={isLight ? "text-neutral-900" : "text-white"}>
              {heading}{" "}
            </span>
            <span className="text-black">{headingHighlight}</span>
          </h2>
          <p
            className={cn(
              "type-body-lg mt-4",
              isLight ? "text-neutral-600" : "text-white"
            )}
          >
            {subtitle}
          </p>
        </div>

        {/* Mobile + Tablet layout (< lg): top/bottom funnel with connectors.
            3 pills above → converge into card → 3 pills below. Both rows' flows
            point INTO the card (sink), matching the heading's promise of
            "scattered data → useful insights". */}
        <div className="mx-auto max-w-[400px] lg:hidden">
          <PillRowMobile
            pills={pillsMobileTop}
            visible={visible}
            position="top"
          />
          <ConnectorMobile
            visible={visible}
            position="top"
            branches={mobileTopBranches}
          />
          <div className="flex justify-center">
            <div
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "scale(1)" : "scale(0.96)",
                transition:
                  "opacity 0.6s cubic-bezier(0.16,1,0.3,1) 0.35s, transform 0.6s cubic-bezier(0.16,1,0.3,1) 0.35s",
              }}
            >
              <CardContent
                visible={visible}
                cardName={cardName}
                cardBiomarkers={cardBiomarkers}
                cardActionPoints={cardActionPoints}
              />
            </div>
          </div>
          <ConnectorMobile visible={visible} position="bottom" />
          <PillRowMobile
            pills={pillsMobileBottom}
            visible={visible}
            position="bottom"
          />
        </div>

        {/* Desktop layout */}
        <div className="relative hidden h-[200px] lg:block">
          {/* Single SVG with all connectors */}
          {visible && (
            <svg
              viewBox="0 0 1000 200"
              className="pointer-events-none absolute inset-0 h-full w-full"
              preserveAspectRatio="none"
              fill="none"
            >
              <defs>
                <path id="sd-lt" pathLength={100} d={L_TOP} />
                <path id="sd-lm" pathLength={100} d={L_MID} />
                <path id="sd-lb" pathLength={100} d={L_BOT} />
                <path id="sd-ls" pathLength={100} d={L_STEM} />
                <path id="sd-rt" pathLength={100} d={R_TOP} />
                <path id="sd-rm" pathLength={100} d={R_MID} />
                <path id="sd-rb" pathLength={100} d={R_BOT} />
                <path id="sd-rs" pathLength={100} d={R_STEM} />
              </defs>

              {/* Stems: base + pulse */}
              <use href="#sd-ls" className="sd-base" />
              <use href="#sd-ls" className="sd-stem" />
              <use href="#sd-rs" className="sd-base" />
              <use href="#sd-rs" className="sd-stem" />

              {/* Branches: base + pulse */}
              {[...leftBranchIds, "sd-rt", "sd-rm", "sd-rb"].map((id) => (
                <g key={id}>
                  <use href={`#${id}`} className="sd-base" />
                  <use href={`#${id}`} className="sd-flow" />
                </g>
              ))}
            </svg>
          )}

          {/* Connector line styles injected once when visible */}

          {/* Pills — left */}
          {pillsLeft.map((pill, i) => (
            <PillDesktop
              key={pill.label}
              pill={pill}
              side="left"
              index={i}
              visible={visible}
            />
          ))}

          {/* Pills — right */}
          {pillsRight.map((pill, i) => (
            <PillDesktop
              key={pill.label}
              pill={pill}
              side="right"
              index={i}
              visible={visible}
            />
          ))}

          {/* Center card */}
          <div
            className="absolute left-1/2 z-10"
            style={{
              top: "50%",
              transform: visible
                ? "translate(-50%, -50%)"
                : "translate(-50%, calc(-50% + 24px))",
              opacity: visible ? 1 : 0,
              transition:
                "opacity 0.8s cubic-bezier(0.16,1,0.3,1), transform 0.8s cubic-bezier(0.16,1,0.3,1)",
            }}
          >
            <CardContent
              visible={visible}
              cardName={cardName}
              cardBiomarkers={cardBiomarkers}
              cardActionPoints={cardActionPoints}
            />
          </div>
        </div>
      </div>
    </>
  )
}

/* ── Card (shared mobile + desktop) ── */
function CardContent({
  cardName,
  cardBiomarkers,
  cardActionPoints,
}: {
  visible?: boolean
  cardName: string
  cardBiomarkers: string
  cardActionPoints: string
}) {
  return (
    <div
      className="flex w-full max-w-[320px] gap-4"
      style={{
        background: "#fff",
        borderRadius: 20,
        padding: "20px 24px",
        boxShadow: "0 10px 40px rgba(0,0,0,0.05)",
      }}
    >
      <div
        className="h-[110px] w-[90px] flex-shrink-0 overflow-hidden rounded-[14px] bg-neutral-100"
        style={{ boxShadow: "rgba(0,0,0,0.12) 0px 4px 12px 0px" }}
      >
        <Image
          src={blobMediaUrl("/images/avatars/ai-person.png")}
          alt=""
          width={180}
          height={220}
          className="h-full w-full object-cover"
        />
      </div>
      <div className="flex flex-1 flex-col justify-center">
        <p className="type-body-xl text-deep-green-900 mb-1.5 font-medium">
          {cardName}
        </p>
        <p className="type-body-sm mb-3 text-neutral-700">
          <span className="type-body-lg">47</span> {cardBiomarkers}
        </p>

        {/* Progress bar */}
        <svg
          viewBox="0 0 150 6"
          className="mb-3.5 block w-full"
          style={{ height: 6, overflow: "visible" }}
          preserveAspectRatio="none"
        >
          <path
            d="M 3 3 L 120 3"
            stroke="rgb(142,155,21)"
            strokeWidth="4"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M 127 3 L 147 3"
            stroke="rgba(72,33,74,0.8)"
            strokeWidth="4"
            strokeLinecap="round"
            fill="none"
          />
        </svg>

        <div className="type-body-sm inline-flex w-full items-center gap-2 rounded-[10px] bg-[#4b3c5a] px-3.5 py-2 text-white">
          <span className="bg-bright-green-400 h-[6px] w-[6px] rounded-full" />
          {cardActionPoints}
        </div>
      </div>
    </div>
  )
}

/* ── Mobile pill row (3-col grid so connector x-centers stay stable) ── */
function PillRowMobile({
  pills,
  visible,
  position,
}: {
  pills: { label: string; icon: IconComponent }[]
  visible: boolean
  position: "top" | "bottom"
}) {
  const offset = position === "top" ? -8 : 8
  return (
    <div className="-mx-1 grid grid-cols-3 gap-1 sm:gap-2">
      {pills.map((pill, i) => {
        const delay = 0.15 + i * 0.08 + (position === "bottom" ? 0.5 : 0)
        const Icon = pill.icon
        return (
          <div
            key={pill.label}
            className={`text-foreground inline-flex items-center gap-1 justify-self-center${
              // A two-pill row skips the middle column so the pills stay over
              // the outer connector branches.
              pills.length === 2 && i === 1 ? " col-start-3" : ""
            }`}
            style={{
              background: "rgb(235,244,153)",
              fontSize: 11,
              fontWeight: 500,
              lineHeight: 1.15,
              padding: "6px 8px",
              borderRadius: 20,
              textAlign: "center",
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : `translateY(${offset}px)`,
              transition: `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
            }}
          >
            <span
              className="flex shrink-0 items-center justify-center"
              style={{ width: 14, height: 14 }}
              aria-hidden="true"
            >
              <Icon size={14} />
            </span>
            <span className="whitespace-nowrap">{pill.label}</span>
          </div>
        )
      })}
    </div>
  )
}

/* ── Mobile connector SVG: branches converging into the card ── */
function ConnectorMobile({
  visible,
  position,
  branches = ["l", "m", "r"],
}: {
  visible: boolean
  position: "top" | "bottom"
  /** Which column centers have a pill above/below them (100 / 300 / 500). */
  branches?: ("l" | "m" | "r")[]
}) {
  /* viewBox 0..600 wide, 0..60 tall; non-uniform scale to container.
     Column centers: 100, 300, 500. Convergence: (300, 50). Stem to card: (300, 60).
     For "top": pill-bottom (y=0) → card-top (y=60).
     For "bottom": vertical flip so flow still reads INTO the card (card above). */
  const flip =
    position === "bottom" ? "scale(1,-1) translate(0,-60)" : undefined
  const delay = position === "top" ? 0.25 : 0.55
  return (
    <div
      className="w-full"
      style={{
        height: 44,
        opacity: visible ? 1 : 0,
        transition: `opacity 0.6s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      }}
    >
      <svg
        viewBox="0 0 600 60"
        className="h-full w-full"
        preserveAspectRatio="none"
        fill="none"
        aria-hidden="true"
      >
        <defs>
          <path
            id={`sd-m-l-${position}`}
            pathLength={100}
            d="M 100 0 L 100 15 C 100 35 300 35 300 50"
          />
          <path
            id={`sd-m-m-${position}`}
            pathLength={100}
            d="M 300 0 L 300 50"
          />
          <path
            id={`sd-m-r-${position}`}
            pathLength={100}
            d="M 500 0 L 500 15 C 500 35 300 35 300 50"
          />
          <path
            id={`sd-m-stem-${position}`}
            pathLength={100}
            d="M 300 50 L 300 60"
          />
        </defs>
        <g transform={flip}>
          {[
            ...branches.map((b) => `sd-m-${b}-${position}`),
            `sd-m-stem-${position}`,
          ].map((id) => (
            <g key={id}>
              <use href={`#${id}`} className="sd-m-base" />
              {visible && <use href={`#${id}`} className="sd-m-flow" />}
            </g>
          ))}
        </g>
      </svg>
    </div>
  )
}

/* ── Desktop pill ── */
function PillDesktop({
  pill,
  side,
  index,
  visible,
}: {
  pill: {
    label: string
    icon: IconComponent
    top: number
    left?: string
    right?: string
  }
  side: "left" | "right"
  index: number
  visible: boolean
}) {
  const [show, setShow] = useState(false)
  const delay = (side === "left" ? [300, 450, 600] : [400, 550, 700])[index]
  const Icon = pill.icon

  useEffect(() => {
    if (!visible) return
    const t = setTimeout(() => setShow(true), delay)
    return () => clearTimeout(t)
  }, [visible, delay])

  return (
    <div
      className="type-body-md text-deep-green-900 absolute z-[15] inline-flex items-center gap-2 rounded-[40px] px-[18px] py-2.5 font-medium whitespace-nowrap"
      style={{
        top: pill.top,
        ...(side === "left" ? { right: pill.right } : { left: pill.left }),
        background: "rgb(235,244,153)",
        minWidth: 130,
        opacity: show ? 1 : 0,
        transform: show
          ? "translateX(0)"
          : `translateX(${side === "left" ? "-30px" : "30px"})`,
        transition:
          "opacity 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1)",
      }}
    >
      <Icon size={16} />
      {pill.label}
    </div>
  )
}
